import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { FinanceTransactionsResponseDto } from '@/features/finances/types/finances-dto';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useFinancesPageFilter } from '@/features/finances/store/useFinancesPageFilter';
import { useAxiosAuth } from '@/hooks/useAxiosAuth.ts';
import { useCurrencyStore } from '@/store/useCurrencyStore.ts';

type Props = {
    apartmentId?: string;
    ownerId?: string;
};

export const useFinancesTransactionsApi = ({
    apartmentId: outerApartmentId,
    ownerId,
}: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useFinancesPageFilter();
    const { currentCurrency } = useCurrencyStore();
    const { startDate, endDate, apartmentId } = filter;
    const apartmentIds = apartmentId?.map((apartment) => apartment.id);

    const {
        data: transactionsData,
        isLoading: isLoadingTransactionsData,
        isError: isErrorTransactionsData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.features.finances.queryKeys.transactions,
            startDate,
            endDate,
            apartmentIds,
            currentCurrency,
            outerApartmentId,
            ownerId,
        ],
        queryFn: async (): Promise<FinanceTransactionsResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.features.finances.endpoints.transactions,
                {
                    startDate: DateTimeHelper.getDateOnly(filter?.startDate),
                    endDate: DateTimeHelper.getDateOnly(filter?.endDate),
                    apartmentId: outerApartmentId ?? apartmentIds,
                    ownerId,
                },
            );
            const response = await axiosInstance.get<FinanceTransactionsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        transactionsData,
        isLoadingTransactionsData,
        isErrorTransactionsData,
    };
};
